<script>
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsappFreeChannelAPI, {
  extractResponseError,
} from 'dashboard/api/channel/whatsappFree';

const KNOWN_STATUS_KEYS = [
  'STARTING',
  'CONNECTING',
  'QR',
  'CONNECTED',
  'DISCONNECTED',
  'UNKNOWN',
];
const POLL_INTERVAL = 3000;

export default {
  components: {
    SettingsFieldSection,
    NextButton,
    Spinner,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      runtime: null,
      qrImage: '',
      lastQrValue: '',
      isLoading: false,
      isPolling: false,
      isReconnecting: false,
      isDisconnecting: false,
      errorMessage: '',
      pollTimer: null,
    };
  },
  computed: {
    sessionStatus() {
      return this.runtime?.session?.status || 'unknown';
    },
    statusLabel() {
      const key = String(this.sessionStatus || 'unknown').toUpperCase();
      const safeKey = KNOWN_STATUS_KEYS.includes(key) ? key : 'UNKNOWN';
      return this.$t(`INBOX_MGMT.ADD.WHATSAPP_FREE.STATUS.${safeKey}`);
    },
  },
  mounted() {
    this.loadRuntime();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    startPolling() {
      if (this.sessionStatus === 'connected') {
        this.stopPolling();
        return;
      }

      this.stopPolling();
      this.pollTimer = setInterval(() => this.refreshQr(), POLL_INTERVAL);
    },
    async renderQr(qr) {
      if (!qr || qr === this.lastQrValue) return;

      this.lastQrValue = qr;
      this.qrImage = await QRCode.toDataURL(qr, {
        width: 240,
        margin: 2,
      });
    },
    async loadRuntime() {
      if (!this.inbox?.id) return;

      this.isLoading = true;
      this.errorMessage = '';
      try {
        const response = await WhatsappFreeChannelAPI.showInbox(this.inbox.id);
        this.runtime = response;
        if (this.sessionStatus !== 'connected') {
          await this.refreshQr();
          this.startPolling();
        } else {
          this.stopPolling();
        }
      } catch (error) {
        this.errorMessage = extractResponseError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async refreshQr() {
      if (!this.inbox?.id || this.sessionStatus === 'connected') {
        this.stopPolling();
        return;
      }
      if (this.isPolling) return;

      this.isPolling = true;
      try {
        const qrResponse = await WhatsappFreeChannelAPI.inboxQr(this.inbox.id);
        this.runtime = qrResponse;
        await this.renderQr(qrResponse.qr);

        if (this.sessionStatus === 'connected') {
          this.stopPolling();
        }
      } catch (error) {
        this.errorMessage = extractResponseError(error);
      } finally {
        this.isPolling = false;
      }
    },
    async reconnect() {
      if (!this.inbox?.id) return;

      this.isReconnecting = true;
      this.errorMessage = '';
      this.qrImage = '';
      this.lastQrValue = '';

      try {
        const response = await WhatsappFreeChannelAPI.inboxReconnect(
          this.inbox.id
        );
        this.runtime = response;
        useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP_FREE.RECONNECT_STARTED'));
        await this.loadRuntime();
        this.startPolling();
      } catch (error) {
        this.errorMessage = extractResponseError(error);
        useAlert(this.errorMessage);
      } finally {
        this.isReconnecting = false;
      }
    },
    async disconnect() {
      if (!this.inbox?.id) return;

      this.isDisconnecting = true;
      this.errorMessage = '';
      this.qrImage = '';
      this.lastQrValue = '';
      this.stopPolling();

      try {
        const response = await WhatsappFreeChannelAPI.inboxDisconnect(
          this.inbox.id
        );
        this.runtime = response;
        useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP_FREE.DISCONNECT_SUCCESS'));
        await this.loadRuntime();
      } catch (error) {
        this.errorMessage = extractResponseError(error);
        useAlert(this.errorMessage);
      } finally {
        this.isDisconnecting = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.CONFIGURATION.STATUS_TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.CONFIGURATION.STATUS_HELP')"
    >
      <div class="flex flex-col gap-4">
        <div class="inline-flex items-center gap-2 text-sm text-n-slate-12">
          <span class="w-2 h-2 rounded-full bg-n-brand" />
          <span>{{ statusLabel }}</span>
          <Spinner v-if="isLoading || isPolling" class="!w-4 !h-4" />
        </div>

        <div v-if="qrImage" class="flex items-center gap-4">
          <div
            class="flex items-center justify-center w-60 h-60 rounded-xl bg-white outline outline-1 -outline-offset-1 outline-n-weak"
          >
            <img
              :src="qrImage"
              :alt="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.QR_ALT')"
              class="w-52 h-52"
            />
          </div>
        </div>

        <p v-if="errorMessage" class="text-sm text-n-ruby-11">
          {{ errorMessage }}
        </p>

        <div class="flex flex-wrap gap-3">
          <NextButton
            type="button"
            outline
            slate
            icon="i-lucide-refresh-cw"
            :is-loading="isLoading"
            :label="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.REFRESH_BUTTON')"
            @click="loadRuntime"
          />
          <NextButton
            type="button"
            solid
            blue
            icon="i-lucide-rotate-cw"
            :is-loading="isReconnecting"
            :label="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.RECONNECT_BUTTON')"
            @click="reconnect"
          />
          <NextButton
            type="button"
            ghost
            ruby
            icon="i-lucide-unlink"
            :is-loading="isDisconnecting"
            :label="$t('INBOX_MGMT.ADD.WHATSAPP_FREE.DISCONNECT_BUTTON')"
            @click="disconnect"
          />
        </div>
      </div>
    </SettingsFieldSection>
  </div>
</template>
